import { useCallback, useContext } from 'react';
import { z } from 'zod';
import { createId } from '@paralleldrive/cuid2';
import { type BrandInsightsPageContext, type ChatContextItem, ChatContextType } from '@trinity/chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  CustomPageChatContextIdentifier,
  type CustomPageAction,
  type CustomPageActionSubject,
  type CustomPageDispatchAction,
} from '@trinity/custom-pages-registry';
import { ChatStateDispatchContext } from '../../Chat.State/context';
import type { ChatState } from '../../Chat.State/interfaces';
import { ActiveChatSessionContext, ChatContextItemsContext } from '../context';
import { useSetChatInputText } from './useSetChatInputText';
import { useSelectedQueryParentIdentifier } from './useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './useSubmitQueryMutation';

const EMPTY_BATCH_ACTION: ChatState.BatchAction = {
  type: 'batch-actions',
  payload: {
    actions: [],
  },
};

type SubjectPayload = {
  subject: CustomPageActionSubject;
};

type OpenNewChatSubjectPayload = {
  subject: CustomPageActionSubject;
  pendingQuery?: string | null;
  chatContextIdentifier: CustomPageChatContextIdentifier;
};

type UpdateInputPayload = {
  value: string;
};

type SubmitQueryPayload = {
  query: string;
};

const customPageActionSubjectSchema = z
  .strictObject({
    id: z.union([z.number(), z.string()]),
    kind: z.string().optional(),
    link: z.string().optional(),
    name: z.string(),
  })
  .transform(
    (value): CustomPageActionSubject => ({
      id: value.id,
      name: value.name,
      ...(value.kind !== undefined ? { kind: value.kind } : {}),
      ...(value.link !== undefined ? { link: value.link } : {}),
    })
  );

const subjectPayloadSchema = z
  .strictObject({
    subject: customPageActionSubjectSchema,
  })
  .transform(
    (value): SubjectPayload => ({
      subject: value.subject,
    })
  );

const openNewChatSubjectPayloadSchema = z
  .strictObject({
    subject: customPageActionSubjectSchema,
    chatContextIdentifier: z.nativeEnum(CustomPageChatContextIdentifier),
    pendingQuery: z.string().nullable().optional(),
  })
  .transform(
    (value): OpenNewChatSubjectPayload => ({
      subject: value.subject,
      chatContextIdentifier: value.chatContextIdentifier,
      pendingQuery: value.pendingQuery,
    })
  );

const updateInputPayloadSchema = z
  .strictObject({
    value: z.string(),
  })
  .transform(
    (value): UpdateInputPayload => ({
      value: value.value,
    })
  );

const submitQueryPayloadSchema = z
  .strictObject({
    query: z.string(),
  })
  .transform(
    (value): SubmitQueryPayload => ({
      query: value.query,
    })
  );

function mapContextItemToPageContext(item: ChatContextItem): BrandInsightsPageContext.Item {
  if (item.agent.identifier === (ChatContextType.Global as string)) {
    return {
      type: ChatContextType.Global,
    };
  }

  return {
    type: ChatContextType.Agent,
    data: item,
    metadata: {},
  };
}

function resolveOpenNewChatContext(
  contextItems: ChatContextItem[],
  payload: {
    subject: CustomPageActionSubject;
    chatContextIdentifier: CustomPageChatContextIdentifier;
  }
): BrandInsightsPageContext.Item | null {
  const selectedContextItem = contextItems.find((item) => item.identifier === payload.chatContextIdentifier);

  if (!selectedContextItem) {
    return null;
  }

  const baseContext = mapContextItemToPageContext(selectedContextItem);

  if (baseContext.type !== ChatContextType.Agent) {
    return baseContext;
  }

  if (typeof baseContext.data !== 'object' || baseContext.data === null) {
    return null;
  }

  return {
    ...baseContext,
    data: {
      ...baseContext.data,
      subject: payload.subject,
    },
  };
}

function handleUpdateInputAction(args: {
  actionPayload: unknown;
  dispatch: React.Dispatch<ChatState.Action>;
  setChatInputText: (value: string, options?: { focus?: boolean }) => void;
}) {
  const { actionPayload, dispatch, setChatInputText } = args;

  const payloadParseResult = updateInputPayloadSchema.safeParse(actionPayload);
  if (!payloadParseResult.success) {
    dispatch(EMPTY_BATCH_ACTION);
    return;
  }

  setChatInputText(payloadParseResult.data.value, { focus: true });
}

export function useCustomPageActionDispatch(): CustomPageDispatchAction {
  const dispatch = useContext(ChatStateDispatchContext);
  const chat = useContext(ActiveChatSessionContext);
  const contextItems = useContext(ChatContextItemsContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const setChatInputText = useSetChatInputText();
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const submitQueryMutation = useSubmitQueryMutation();

  return useCallback(
    (action: CustomPageAction) => {
      switch (action.type) {
        case 'update-subject': {
          const payloadParseResult = subjectPayloadSchema.safeParse(action.payload);
          if (chat.context.type !== ChatContextType.Agent || !payloadParseResult.success) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          dispatch({
            type: 'chat-instance-subject-updated',
            payload: {
              subject: payloadParseResult.data.subject,
            },
          });
          return;
        }

        case 'open-new-chat-for-subject': {
          const payloadParseResult = openNewChatSubjectPayloadSchema.safeParse(action.payload);
          if (!brandIdentifier || !payloadParseResult.success) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          const context = resolveOpenNewChatContext(contextItems, payloadParseResult.data);
          if (!context) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          dispatch({
            type: 'new-chat-started',
            payload: {
              brandIdentifier,
              context,
              pendingQuery: payloadParseResult.data.pendingQuery ?? null,
            },
          });
          return;
        }

        case 'update-input': {
          handleUpdateInputAction({
            actionPayload: action.payload,
            dispatch,
            setChatInputText,
          });
          return;
        }

        case 'submit-query': {
          const payloadParseResult = submitQueryPayloadSchema.safeParse(action.payload);
          if (!payloadParseResult.success) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          const query = payloadParseResult.data.query.trim();
          if (!query.length) {
            dispatch(EMPTY_BATCH_ACTION);
            return;
          }

          const parentQueryIdentifier = getSelectedQueryParentIdentifier() ?? null;
          submitQueryMutation.mutate({
            identifier: createId(),
            parentQueryIdentifier,
            query,
            action: null,
          });
          return;
        }

        case 'noop':
        default:
          // Stub mapping: custom page actions currently no-op in chat state.
          dispatch(EMPTY_BATCH_ACTION);
      }
    },
    [
      brandIdentifier,
      chat.context,
      contextItems,
      dispatch,
      getSelectedQueryParentIdentifier,
      setChatInputText,
      submitQueryMutation,
    ]
  );
}
